import type { TossPaymentsWidgets, WidgetPaymentMethodWidget } from "@tosspayments/tosspayments-sdk";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { t } from "i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { createDraftOrder } from "@repo/shopify/api/createDraftOrder";
import useCartInfo from "@repo/shopify/hooks/useCartInfo";
import useCustomerInfo from "@repo/shopify/hooks/useCustomerInfo";
import { getStoredGoogleCampaignId, getStoredGoogleTrafficSource } from "@repo/shopify/hooks/useGoogleTrafficSource";
import { getStoredMetaCampaignId, getStoredMetaTrafficSource } from "@repo/shopify/hooks/useMetaTrafficSource";
import { getStoredNaverCampaignId, getStoredNaverTrafficSource } from "@repo/shopify/hooks/useNaverTrafficSource";
import useTracking from "@repo/shopify/hooks/useTracking";
import { BLOCK_ADDRESS, isValidAddress } from "@repo/shopify/utils/address";
import { checkQuantityLimit } from "@repo/shopify/utils/cart";
import { extractId } from "@repo/shopify/utils/common";
import { formatPhone } from "@repo/shopify/utils/format";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Loading from "@atoms/Loading";
import Price from "@atoms/Price";

import CheckoutLineItem from "@molecules/CheckoutLineItem";
import Discounts from "@molecules/Discounts";

import ShippingAddress from "@organisms/Address/ShippingAddress";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCheckPurchaseLimit from "@hooks/useCheckPurchaseLimit";
import useFormErrorHandler from "@hooks/useFormErrorHandler";

import useCart from "@queries/useCart";
import useCustomer from "@queries/useCustomer";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

interface IFormData {
  discountCode: string;
}

const CLIENT_KEY = import.meta.env.VITE_REACT_APP_TOSS_CLIENT_KEY as string;
const TINT_PAY_PARAM = "TINT_PAY_PARAM";

const modalTitles: Record<string, string> = {
  paymentError: "Payment Error",
  invalidCart: "Checkout Error",
  checkoutError: "Checkout Error",
  overQuantity: "Quantity Limit",
};

export default function CheckoutKr() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 토스 임직원 구매 한도 체크
  const { remainingLimit, expectedRemainingLimit } = useCheckPurchaseLimit();

  const { gtmBeginCheckout } = useTracking();

  const { cart, isSyncing } = useCartStore();
  const { customer, token, logout } = useCustomerStore();
  const { openToast, openModal, openAccount } = useLayoutStore();

  const { fetchCustomer } = useCustomer();
  const { updateCartLine, discountCart, discountCartLoading } = useCart();

  const { isEmployee, isPartnership, membership, isMembershipDiscountCodeUsed, addresses, codeUsableMembership } = useCustomerInfo(
    customer,
    "thisisneverthat",
  );

  const {
    totalAmount,
    shippingAmount,
    originTotalAmount,
    autoDiscountAmount,
    codeDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    orderName,
  } = useCartInfo(cart);

  const [isInitLoading, setIsInitLoading] = useState(true); // 초기 로딩
  const [isLoading, setIsLoading] = useState<boolean>(false); // 주문 생성 로딩
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false); // 결제 요청 로딩
  const [selectedAddressId, setSelectedAddressId] = useState<string>(customer?.defaultAddress?.id || ""); // 선택된 주소 ID
  const [isDiscountCodeApplied, setIsDiscountCodeApplied] = useState(false); // 할인 코드 적용 여부

  const tossPaymentsRef = useRef<TossPaymentsWidgets | null>(null);
  const tossPaymentsWidgetRef = useRef<WidgetPaymentMethodWidget | null>(null);
  const initStartedRef = useRef(false); // isSyncing이 꺼진 뒤 init()을 1회만 실행하기 위함

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<IFormData>({ defaultValues: { discountCode: "" }, mode: "onChange" });

  const { discountCode } = watch();

  const { onCommonError } = useFormErrorHandler({ values: getValues(), errors });

  useEffect(() => {
    // 로그인 직후 체크아웃에 진입하면 useCartSync의 카트 복원/병합이 아직 끝나지 않았을 수 있어서 예외처리함
    if (isSyncing || initStartedRef.current) return;
    initStartedRef.current = true;

    void init();

    return () => {
      void applyDiscountCode("");
    };
  }, [isSyncing]);

  const init = async () => {
    try {
      const isSearchParamsHandled = await handleSearchParams();
      if (isSearchParamsHandled) return;

      const isCustomerValid = await checkCustomer();
      if (!isCustomerValid) return;

      const { customer: latestCustomer } = useCustomerStore.getState();
      gtmBeginCheckout(cart, latestCustomer.id ? extractId(latestCustomer.id) : undefined, latestCustomer.email ?? undefined);

      await applyDiscountCode("");

      await initializeTossWidget();

      const data = await updateCartLine({ cartId: cart.id, lines: cart.lines.edges.map(({ node }) => ({ id: node.id })) });

      if (data?.cartLinesUpdate.cart?.lines?.edges.some(({ node }) => node.quantity <= 0)) {
        await updateCartLine({
          cartId: cart.id,
          lines: data?.cartLinesUpdate.cart.lines.edges.map(({ node }) => ({ id: node.id })),
        });
      }
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      navigate(URLS.HOME);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsInitLoading(false);
    }
  };

  const handleSearchParams = async () => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const amount = searchParams.get("amount");
    const errorMessage = searchParams.get("message");

    console.log("[THISISNEVERTHAT KR] SEARCH PARAMS", { orderId, paymentKey, amount, errorMessage });

    if (errorMessage) {
      setTimeout(() => {
        openValidationModal("paymentError", errorMessage);
      }, 300);
      return false;
    }

    if (paymentKey && orderId && amount) {
      await completePaymentRequest({ paymentKey, amount, orderId });
      return true;
    }

    return false;
  };

  const checkCustomer = async () => {
    // 로그인 하지 않은 경우
    if (!token.accessToken) {
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 300);
      return false;
    }

    // 유효하지 않은 토큰 (소셜로그인 동시접속 등으로 인해 토큰 만료)
    const data = await fetchCustomer({ customerAccessToken: token.accessToken });
    if (!data?.customer) {
      openToast(NOTIFICATIONS.WARNING_END_SESSION);
      navigate(URLS.HOME);
      logout();
      setTimeout(() => {
        openAccount(true);
      }, 300);
      return false;
    }

    return true;
  };

  const initializeTossWidget = async () => {
    if (tossPaymentsWidgetRef.current) await tossPaymentsWidgetRef.current.destroy();

    const tossPayments = await loadTossPayments(CLIENT_KEY);

    tossPaymentsRef.current = tossPayments.widgets({ customerKey: extractId(useCustomerStore.getState().customer.id) });
    await tossPaymentsRef.current.setAmount({ value: 99999, currency: "KRW" });

    tossPaymentsWidgetRef.current = await tossPaymentsRef.current.renderPaymentMethods({
      selector: "#payment-method",
      variantKey: "TNT_KR_default",
    });
  };

  // 카트 검증
  useEffect(() => {
    // 토스 결제 후 리다이렉트로 재진입하면 카트는 id만 persist 되어 있어 복구 전까지 비어 있으므로 init이 끝나기 전에는 검사하지 않음
    if (isLoading || isInitLoading) return;

    const paymentParam = sessionStorage.getItem(TINT_PAY_PARAM);
    if (!paymentParam && !cart.lines.edges.length) {
      openToast(NOTIFICATIONS.WARNING_NO_CART);
      navigate(URLS.HOME);
    }

    // 토스 파트너십 태그가 있는 고객의 경우, 구매 한도 초과시 홈으로 이동
    if (isPartnership) {
      if (remainingLimit <= 0 || expectedRemainingLimit < 0) {
        openToast(NOTIFICATIONS.WARNING_EXCEEDED_PURCHASE_LIMIT);
        navigate(URLS.HOME);
      }
    }
  }, [cart, isLoading, isInitLoading, isPartnership, remainingLimit, expectedRemainingLimit]);

  useEffect(() => {
    if (selectedAddressId || !customer?.defaultAddress?.id) return;
    setSelectedAddressId(customer.defaultAddress.id);
  }, [customer?.defaultAddress?.id]);

  // 선택된 주소
  const selectedAddress = useMemo(
    () => addresses?.find((address) => extractId(address.id) === extractId(selectedAddressId)),
    [addresses, selectedAddressId],
  );

  // 주문생성 API 호출 Parameter 데이터
  const getLambdaPaymentParam = () => {
    if (!cart.lines.edges.length) return null;

    const orderDiscountCode = cart.discountCodes.find((item) => item.applicable)?.code || "";

    const address = {
      firstName: selectedAddress?.firstName || "",
      lastName: selectedAddress?.lastName || "",
      phone: formatPhone(selectedAddress?.phone || ""),
      zip: selectedAddress?.zip || "",
      address1: selectedAddress?.address1 || "",
      address2: selectedAddress?.address2 || "",
      country: selectedAddress?.country || "South Korea",
      province: selectedAddress?.province || "",
      city: selectedAddress?.city || "-",
      company: "",
    };

    const googleTrafficSource = getStoredGoogleTrafficSource();
    const googleCampaignId = getStoredGoogleCampaignId();
    const naverTrafficSource = getStoredNaverTrafficSource();
    const naverCampaignId = getStoredNaverCampaignId();
    const metaTrafficSource = getStoredMetaTrafficSource();
    const metaCampaignId = getStoredMetaCampaignId();

    return {
      orderData: {
        purchasingEntity: { customerId: customer.id },
        shippingLine: { shippingRateHandle: "CJ 대한통운" },
        lineItems: cart.lines.edges.map(({ node }) => ({
          variantId: node.merchandise.id,
          quantity: node.quantity,
        })),
        shippingAddress: address,
        billingAddress: address,
        customAttributes: orderDiscountCode ? [{ key: "DiscountCode", value: orderDiscountCode }] : [],
        acceptAutomaticDiscounts: true,
        discountCodes: orderDiscountCode ? [orderDiscountCode] : [],
        tags: [
          ...(isEmployee && !isPartnership ? ["Employee"] : []),
          ...(membership === orderDiscountCode.toUpperCase() ? ["멤버십_할인코드"] : []),
          ...(googleTrafficSource === "google_ads" ? ["유입_GoogleAds"] : []),
          ...(googleTrafficSource === "google_shopping" ? ["유입_GoogleShopping"] : []),
          ...(googleCampaignId ? [`유입_GoogleAds_${googleCampaignId}`.slice(0, 40)] : []),
          ...(naverTrafficSource === "naver_shopping" ? ["유입_NaverShopping"] : []),
          ...(naverTrafficSource === "naver_paid_shopping" ? ["유입_NaverPaidShopping"] : []),
          ...(naverCampaignId ? [`유입_NaverPaidShopping_${naverCampaignId}`.slice(0, 40)] : []),
          ...(metaTrafficSource === "meta_ads" ? ["유입_MetaAds"] : []),
          ...(metaCampaignId ? [`유입_MetaAds_${metaCampaignId}`.slice(0, 40)] : []),
        ],
      },
    };
  };

  const onSubmit: SubmitHandler<IFormData> = async (formData) => {
    console.log("[THISISNEVERTHAT] KR DISCOUNT CODE SUBMIT", {
      isDiscountCodeApplied,
      discountCodeValue: formData.discountCode,
    });
    if (isDiscountCodeApplied) {
      await applyDiscountCode("");
    } else {
      const discountCodeValue = formData.discountCode.trim();
      await addDiscountCode(discountCodeValue);
    }
  };

  // 토스 결제 위젯 요청
  const requestPaymentWidget = async () => {
    if (!tossPaymentsRef.current || !tossPaymentsWidgetRef.current) {
      setIsPaymentLoading(false);
      return;
    }

    const lambdaPaymentParam = getLambdaPaymentParam();
    console.log("[TINT KR] REQUEST PARAM", lambdaPaymentParam);
    if (!lambdaPaymentParam) {
      setIsPaymentLoading(false);
      return;
    }

    sessionStorage.setItem(TINT_PAY_PARAM, JSON.stringify(lambdaPaymentParam));

    try {
      await tossPaymentsRef.current.setAmount({ value: totalAmount, currency: "KRW" });
      await tossPaymentsRef.current?.requestPayment({
        orderId: `${extractId(customer.id)}_${Number(new Date())}`,
        orderName,
        customerName: customer.email || "",
        customerEmail: customer.email || "",
        successUrl: window.location.href,
        failUrl: window.location.href,
      });
    } catch (error: any) {
      openModal({
        title: "Payment Canceled",
        content: <p>{t("payment_cancel")}</p>,
        confirm: {
          label: "Okay",
          onClick: () => {
            openModal(null);
          },
        },
      });

      sessionStorage.removeItem(TINT_PAY_PARAM);
      openToast(NOTIFICATIONS.ERROR_SERVER);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`[TOSS] Failed to Fetch : ${message}`);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // 토스 결제 승인 후 주문 생성
  const completePaymentRequest = async ({ paymentKey, orderId, amount }: { paymentKey: string; orderId: string; amount: string }) => {
    setIsLoading(true);

    // 토스 결제 요청했을 때 sessionStorage 저장된 paymentParameter
    const paymentParam = sessionStorage.getItem(TINT_PAY_PARAM);
    const paymentParameter = paymentParam ? JSON.parse(paymentParam) : null;

    console.log("[TINT KR] ORDER PAYMENT PARAM", paymentParameter);

    if (!paymentParameter) {
      setIsLoading(false);
      await initializeTossWidget();
      setSearchParams({});
      openValidationModal("paymentError", t("necessary_payment_data"));
      sessionStorage.removeItem(TINT_PAY_PARAM);
      return;
    }

    try {
      const response = await createDraftOrder({
        shop: "thisisneverthat",
        param: { paymentKey, orderId, amount, ...paymentParameter },
        domain: "ko",
      });

      console.log("[THISISNEVERTHAT KR] ORDER LAMBDA RESPONSE", response);

      if (response?.status === 200) {
        navigate(`/order-completed/${response.data.orderId}`);
        sessionStorage.removeItem(TINT_PAY_PARAM);
      } else {
        setIsLoading(false);
        await initializeTossWidget();
        setSearchParams({});
        openValidationModal("paymentError", response?.data?.message || t("payment_failed"));
        sessionStorage.removeItem(TINT_PAY_PARAM);
      }
    } catch (error) {
      const _message = error instanceof Error ? error.message : String(error);
      setIsLoading(false);
      await initializeTossWidget();
      setSearchParams({});
      openValidationModal("paymentError", t("payment_failed"));
      sessionStorage.removeItem(TINT_PAY_PARAM);
    }
  };

  /* 유효성 검사 */
  const checkBeforePayment = async () => {
    setIsPaymentLoading(true);

    // 0. Get updated customer & Cart
    const updatedCustomer = await fetchCustomer({ customerAccessToken: token.accessToken });
    console.log("[THISISNEVERTHAT KR] CUSTOMER: ", updatedCustomer?.customer);
    if (!updatedCustomer?.customer?.id) {
      setIsPaymentLoading(false);
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      navigate(URLS.HOME);
      return;
    }

    const updatedCart = await updateCartLine({ cartId: cart.id, lines: cart.lines.edges.map(({ node }) => ({ id: node.id })) });
    console.log("[THISISNEVERTHAT KR] CART: ", updatedCart?.cartLinesUpdate.cart);
    if (!updatedCart?.cartLinesUpdate.cart?.lines.edges.length) {
      setIsPaymentLoading(false);
      openToast(NOTIFICATIONS.WARNING_NO_CART);
      navigate(URLS.HOME);
      return;
    }

    // 1. Check buyerIdentity
    if (extractId(updatedCustomer.customer.id) !== extractId(updatedCart?.cartLinesUpdate.cart.buyerIdentity.customer?.id || "")) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("checkout.wrong_customer_id"));
      return;
    }

    // 2. Check address
    if (!selectedAddress) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("necessary_shipping_address"));
      return;
    }

    // 3. Check isValid address
    if (!isValidAddress(selectedAddress)) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("necessary_invalid_shipping_address"));
      return;
    }

    const isBlocked = BLOCK_ADDRESS.some((blockedAddress) => {
      const selectedAddress1 = selectedAddress.address1 ?? "";
      return (
        blockedAddress.zip === selectedAddress.zip &&
        (blockedAddress.roadname_address.includes(selectedAddress1) || blockedAddress.lotnumber_address.includes(selectedAddress1))
      );
    });

    if (isBlocked) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("blocked_address"));
      return;
    }

    const { exceeded, adjustedCartLines } = checkQuantityLimit({
      cart: updatedCart.cartLinesUpdate.cart,
      customer: updatedCustomer.customer,
    });

    if (exceeded) {
      setIsPaymentLoading(false);
      openValidationModal("overQuantity", t("quantity_insufficient"), adjustedCartLines);
      return;
    }

    if (!adjustedCartLines.length) {
      setIsPaymentLoading(false);
      openValidationModal("invalidCart", t("invalid_cart"));
      return;
    }

    // 9. Check discount applicable
    const discountCodes = updatedCart?.cartLinesUpdate.cart.discountCodes || [];
    if (discountCodes.length) {
      const { code, applicable } = discountCodes[0];
      if (code && applicable) {
        const isAppliedCode = await addDiscountCode(code);
        if (!isAppliedCode) {
          setIsPaymentLoading(false);
          openValidationModal("checkoutError", t("checkout.wrong_discount_code"));
          return;
        }
      }
    }

    if (totalAmount !== parseFloat(updatedCart?.cartLinesUpdate?.cart.cost.totalAmount.amount || "0")) {
      setIsPaymentLoading(false);
      openValidationModal("checkoutError", t("checkout.wrong_cart_changed"));
      return;
    }

    await requestPaymentWidget();
  };

  // 유효성 검사 실패시 케이스 별 모달 열기
  const openValidationModal = (type: string, content: string, cartLines?: { id: string; quantity: number }[]) => {
    const title = modalTitles[type];

    const callback = async () => {
      switch (type) {
        case "paymentError":
          setSearchParams({});
          break;
        case "overQuantity":
          if (cartLines) await updateCartLine({ cartId: cart.id, lines: cartLines });
          break;
        case "invalidCart":
          await updateCartLine({ cartId: cart.id, lines: [] });
          navigate(URLS.HOME);
          break;
        default:
          break;
      }
      openModal(null);
    };

    openModal({
      title,
      content: <p>{content}</p>,
      confirm: {
        label: "Okay",
        onClick: () => {
          void callback();
        },
      },
      onClickClose: () => {
        void callback();
      },
    });
  };

  const addDiscountCode = async (appliedCode: string) => {
    // 멤버십 할인코드 사용 했을 경우
    if (appliedCode.toUpperCase() === membership && isMembershipDiscountCodeUsed) {
      setError("discountCode", { type: "manual" });
      setValue("discountCode", "");
      openToast(NOTIFICATIONS.ERROR_DISCOUNT_CODE);
      setIsDiscountCodeApplied(false);
      return false;
    }

    const result = await applyDiscountCode(appliedCode);
    return result;
  };

  const applyDiscountCode = async (appliedCode: string) => {
    const data = await discountCart({ cartId: cart.id, discountCodes: appliedCode ? [appliedCode] : [] });

    console.log(`[THISISNEVERTHAT KR] Discount Code : ${appliedCode}`, data);

    // 할인코드 적용 제거
    if (appliedCode === "") {
      setValue("discountCode", "");
      setIsDiscountCodeApplied(false);
      return false;
    }

    const userErrors = data?.cartDiscountCodesUpdate.userErrors.length;
    const notApplicable = !data?.cartDiscountCodesUpdate?.cart?.discountCodes?.[0]?.applicable;

    if (userErrors || notApplicable) {
      await discountCart({ cartId: cart.id, discountCodes: [] });
      setError("discountCode", { type: "manual" });
      setValue("discountCode", "");
      openToast(NOTIFICATIONS.ERROR_DISCOUNT_CODE);
      setIsDiscountCodeApplied(false);
      return false;
    }

    setValue("discountCode", appliedCode);
    setIsDiscountCodeApplied(true);
    return true;
  };

  return (
    <>
      {isLoading ? <Loading type="page" /> : null}

      <ShippingAddress
        selectedAddress={selectedAddress}
        selectedAddressId={selectedAddressId}
        setSelectedAddressId={setSelectedAddressId}
      />

      {/* PAYMENT METHOD */}
      <div className="flex flex-col gap-0.5">
        <h2>Payment Method</h2>
        <div id="payment-method" />
      </div>

      {/* ORDER SUMMARY */}
      <div className="flex flex-col gap-2.5">
        <h2>Order Summary</h2>
        <div className="tnt-gap-x grid grid-cols-8 gap-y-2.5">
          {/* Product */}
          {cart.lines.edges
            ?.filter(({ node }) => !node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
            .map(({ node }) => (
              <CheckoutLineItem key={node.id} node={node} />
            ))}

          {/* Gift */}
          {cart.lines.edges
            ?.filter(({ node }) => node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
            .map(({ node }) => (
              <CheckoutLineItem key={node.id} node={node} />
            ))}
        </div>
      </div>

      {/* DISCOUNT CODE */}
      {!isEmployee && (
        <div className="flex flex-col gap-1">
          <h2>Discount Code</h2>
          {codeUsableMembership && !isMembershipDiscountCodeUsed ? (
            <h3 className="text-blue">{`*Monthly Discount Code: ${membership}`}</h3>
          ) : null}
          <form className="flex flex-wrap items-start gap-2.5" onSubmit={handleSubmit(onSubmit, onCommonError)}>
            <Input
              {...register("discountCode", { pattern: /^[a-zA-Z0-9-_]+$/ })}
              {...(errors.discountCode && { isError: true })}
              className="min-w-0 flex-1"
              disabled={isInitLoading || discountCartLoading || isDiscountCodeApplied}
              placeholder="Discount Code"
            >
              {discountCode && !discountCartLoading ? (
                <Button
                  as="text"
                  className="absolute top-1/2 right-0 -translate-y-1/2"
                  onClick={() => {
                    if (isDiscountCodeApplied) {
                      void applyDiscountCode("");
                    } else {
                      reset();
                    }
                  }}
                >
                  {isDiscountCodeApplied ? "Remove" : "×"}
                </Button>
              ) : null}
            </Input>
            {isDiscountCodeApplied ? (
              <p className="order-last w-full text-xs font-normal">{t("checkout.discount_code_alert_description")}</p>
            ) : null}
            <Button
              as="square"
              className="shrink-0"
              disabled={isInitLoading || isDiscountCodeApplied || !discountCode || Boolean(errors.discountCode)}
              loading={discountCartLoading}
              type="submit"
              variant="secondary"
            >
              {isDiscountCodeApplied ? t("submitted") : t("submit")}
            </Button>
          </form>
        </div>
      )}

      {/* SUMMARY*/}
      <div className="pc:pb-5 flex flex-col justify-end gap-2.5 pb-4">
        <div className="flex flex-col gap-1">
          <div className="tnt-gap-x grid grid-cols-2">
            <span>Subtotal</span>
            <Price className="justify-end" price={originTotalAmount} />
          </div>
          <div className="tnt-gap-x grid grid-cols-2">
            <span>Shipping</span>
            <Price className="justify-end" price={shippingAmount} />
          </div>

          <Discounts
            autoDiscountAmount={autoDiscountAmount}
            autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
            codeDiscountAmount={codeDiscountAmount}
            codeDiscountTitle={codeDiscountTitle}
          />

          <div className="grid grid-cols-2 gap-x-5">
            <span>Total</span>
            <Price className="justify-end" price={totalAmount} />
          </div>
        </div>
        <Button
          as="square"
          className="h-11.5! w-full"
          disabled={isInitLoading || discountCartLoading || isPaymentLoading}
          loading={isInitLoading || discountCartLoading || isPaymentLoading}
          onClick={() => {
            void checkBeforePayment();
          }}
        >
          {t("checkout.pay_now")}
        </Button>
      </div>
    </>
  );
}
